// Operator console › a company's add-ons (coverage audit §5 row 4, rows 82-83): a drawer over the
// companies table with the add-on rows (Enable / Turn off, as on Setup › Add-ons), a full change
// form (status, source, note: every field of `PUT …/entitlements` the server accepts today; it
// refuses future start dates and end dates until its reconciler exists) and the live rows
// (`GET …/entitlements`). A switch refetches `/v1/me`, so the company's screens follow at once.
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerSection,
  DrawerTitle,
  Field,
  FieldDescription,
  FieldLabel,
  HomeCard,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { EntitlementStatus, MeResponse } from "../../api/types";
import { AddonRow, useSetEntitlement } from "../addons/addons-page";
import { ADDON_CATALOG, type AddonKey, addonSpec } from "../addons/catalog";
import { ENTITLEMENT_LABELS, EntitlementBadge, problemText } from "../ops-parts";
import { entitlementsQuery } from "../ops-queries";
import { LoadError, Loading } from "../screen-parts";

type Source = "plan" | "addon" | "trial" | "manual";
const STATUSES: EntitlementStatus[] = ["active", "trial", "grace", "disabled"];
const SOURCES: Source[] = ["addon", "plan", "trial", "manual"];

export function AddonsDrawer({
  api,
  me,
  company,
  onClose,
}: {
  api: Api;
  me: MeResponse;
  company: { id: string; name: string } | null;
  onClose: () => void;
}) {
  return (
    <Drawer open={company !== null} onOpenChange={(o) => !o && onClose()} width="720px">
      {company ? <AddonsDrawerBody api={api} me={me} company={company} /> : null}
    </Drawer>
  );
}

function AddonsDrawerBody({
  api,
  me,
  company,
}: {
  api: Api;
  me: MeResponse;
  company: { id: string; name: string };
}) {
  const live = useQuery(entitlementsQuery(api, company.id));
  const set = useSetEntitlement(api, company.id);
  const ids = { feature: useId(), status: useId(), source: useId(), note: useId() };
  const [feature, setFeature] = useState<AddonKey>("kpi");
  const [status, setStatus] = useState<EntitlementStatus>("active");
  const [source, setSource] = useState<Source>("addon");
  const [note, setNote] = useState("");
  const spec = addonSpec(feature);
  const enabling = status === "active" || status === "trial";
  const unavailable = enabling && spec?.active === false;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    set.mutate({ feature, status, source, note: note.trim() || null });
  };
  const rows = [...(live.data?.live ?? [])].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );

  return (
    <>
      <DrawerHeader>Companies › {company.name}</DrawerHeader>
      <DrawerBody>
        <DrawerTitle>Add-ons · {company.name}</DrawerTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          Changes take effect now, for everyone in the company. Turning an add-on off deletes no
          data.
        </p>
        {set.isError ? (
          <Notice tone="danger" className="mt-4" role="alert">
            {problemText(set.error, "The add-on could not be switched.")}
          </Notice>
        ) : null}
        <DrawerSection title="Add-ons" className="mt-6">
          <HomeCard title="Catalog" flush>
            {ADDON_CATALOG.map((a) => (
              <AddonRow
                key={a.key}
                spec={a}
                me={me}
                companyId={company.id}
                operator
                graceUntil={
                  live.data?.cache.find((c) => c.feature_key === a.key)?.read_only_until ?? null
                }
                pending={set.isPending && set.variables?.feature === a.key}
                onChange={(s) => set.mutate({ feature: a.key, status: s })}
              />
            ))}
          </HomeCard>
        </DrawerSection>
        <DrawerSection title="Change with a note">
          <form onSubmit={submit} className="grid gap-4" data-slot="entitlement-form">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field>
                <FieldLabel htmlFor={ids.feature}>Add-on</FieldLabel>
                <NativeSelect
                  id={ids.feature}
                  value={feature}
                  onChange={(e) => setFeature(e.target.value as AddonKey)}
                >
                  {ADDON_CATALOG.map((a) => (
                    <NativeSelectOption key={a.key} value={a.key}>
                      {a.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.status}>Status</FieldLabel>
                <NativeSelect
                  id={ids.status}
                  value={status}
                  onChange={(e) => setStatus(e.target.value as EntitlementStatus)}
                >
                  {STATUSES.map((s) => (
                    <NativeSelectOption key={s} value={s}>
                      {ENTITLEMENT_LABELS[s]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.source}>Source</FieldLabel>
                <NativeSelect
                  id={ids.source}
                  value={source}
                  onChange={(e) => setSource(e.target.value as Source)}
                >
                  {SOURCES.map((s) => (
                    <NativeSelectOption key={s} value={s}>
                      {s}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor={ids.note}>Note</FieldLabel>
              <Input
                id={ids.note}
                maxLength={2000}
                value={note}
                placeholder="Why (kept with the entitlement row)"
                onChange={(e) => setNote(e.target.value)}
              />
              <FieldDescription>
                Starts today. Scheduled starts and end dates are not supported by the API yet.
              </FieldDescription>
            </Field>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={set.isPending || unavailable}>
                {set.isPending ? "Saving…" : "Apply"}
              </Button>
              {unavailable ? (
                <span className="text-sm text-muted-foreground" role="status">
                  {spec?.name} is not available yet.
                </span>
              ) : null}
            </div>
          </form>
        </DrawerSection>
        <DrawerSection title="History">
          {live.isPending ? (
            <Loading label="Loading entitlement history" rows={2} />
          ) : live.isError ? (
            <LoadError what="Entitlements" error={live.error} />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No entitlement rows yet.</p>
          ) : (
            <Table data-slot="entitlement-history">
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Add-on</TableHead>
                  <TableHead scope="col">Status</TableHead>
                  <TableHead scope="col">Source</TableHead>
                  <TableHead scope="col">From</TableHead>
                  <TableHead scope="col">Until</TableHead>
                  <TableHead scope="col">Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableHead scope="row" className="font-normal">
                      {addonSpec(r.feature_key as AddonKey)?.name ?? r.feature_key}
                    </TableHead>
                    <TableCell>
                      <EntitlementBadge status={r.status} />
                    </TableCell>
                    <TableCell>{r.source}</TableCell>
                    <TableCell className="tabular-nums">{r.starts_on}</TableCell>
                    <TableCell className="tabular-nums">
                      {r.ends_on ?? (r.grace_until ? `grace ${r.grace_until}` : "—")}
                    </TableCell>
                    <TableCell className="max-w-56 truncate text-muted-foreground">
                      {r.note ?? ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DrawerSection>
      </DrawerBody>
    </>
  );
}
